// Runtime config — fetched from the Cloudflare Worker at startup.
// Keys never baked into the JS bundle; served from worker env vars.
//
// Two modes:
//   Cloudflare Worker (production / wrangler dev):
//     → GET /config.json served by src/worker.ts from CF env vars
//   Vite dev server (local hot-reload):
//     → Falls back to VITE_SUPABASE_URL / VITE_SUPABASE_ANON_KEY
//       from apps/admin-portal/.env.local (gitignored)

interface AppConfig {
  readonly supabaseUrl: string;
  readonly supabaseAnonKey: string;
}

let cached: AppConfig | null = null;

export async function getConfig(): Promise<AppConfig> {
  if (cached) return cached;

  // 1. Try the worker endpoint (Cloudflare Worker / wrangler dev)
  try {
    const response = await fetch('/config.json');
    if (response.ok) {
      const data = (await response.json()) as Record<string, unknown>;
      const url = data['supabaseUrl'];
      const key = data['supabaseAnonKey'];
      // Require non-empty strings — empty means the secret isn't set on the worker
      if (typeof url === 'string' && url.length > 0 && typeof key === 'string' && key.length > 0) {
        cached = { supabaseUrl: url, supabaseAnonKey: key };
        return cached;
      }
    }
  } catch {
    // Worker not running — fall through to Vite dev fallback
  }

  // 2. Vite dev server fallback (VITE_* injected at dev-server start via .env.local)
  const supabaseUrl: unknown = import.meta.env['VITE_SUPABASE_URL'];
  const supabaseAnonKey: unknown = import.meta.env['VITE_SUPABASE_ANON_KEY'];

  if (typeof supabaseUrl === 'string' && typeof supabaseAnonKey === 'string') {
    cached = { supabaseUrl, supabaseAnonKey };
    return cached;
  }

  throw new Error(
    'Supabase config not found. ' +
      'For Vite dev: add VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY to apps/admin-portal/.env.local. ' +
      'For wrangler dev: add them to apps/admin-portal/.dev.vars.',
  );
}
